import { useEffect, useState } from "react";
import { Link, useNavigate, Outlet, NavLink } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Plus, Store, Star, User as UserIcon, Utensils, Sparkles, Bookmark, Megaphone, CalendarCheck, Inbox } from "lucide-react";

const DashboardLayout = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) navigate("/");
  }, [loading, user, navigate]);

  if (loading) return <div className="text-center py-20 text-muted-foreground">Memuat...</div>;
  if (!user) return null;

  const links = [
    { to: "/dashboard", label: "Beranda", icon: Store, end: true, tid: "dash-home" },
    { to: "/dashboard/restaurants", label: "Restoran Saya", icon: Utensils, tid: "dash-restaurants" },
    { to: "/dashboard/featured", label: "Featured Spots", icon: Sparkles, tid: "dash-featured" },
    { to: "/dashboard/ads", label: "Banner Ads", icon: Megaphone, tid: "dash-ads" },
    { to: "/dashboard/bookings-incoming", label: "Booking Masuk", icon: Inbox, tid: "dash-bookings-in" },
    { to: "/dashboard/reviews", label: "Review Saya", icon: Star, tid: "dash-reviews" },
    { to: "/dashboard/bookings", label: "Booking Saya", icon: CalendarCheck, tid: "dash-bookings" },
    { to: "/dashboard/bookmarks", label: "Favorit", icon: Bookmark, tid: "dash-bookmarks" },
    { to: "/dashboard/profile", label: "Profil", icon: UserIcon, tid: "dash-profile" },
  ];

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 grid grid-cols-1 lg:grid-cols-12 gap-8" data-testid="dashboard">
      <aside className="lg:col-span-3">
        <div className="warm-card p-5 sticky top-24">
          <div className="flex items-center gap-3 mb-5 pb-5 border-b border-border">
            <img src={user.picture || `https://ui-avatars.com/api/?name=${user.name}`} alt="" className="w-12 h-12 rounded-full object-cover" />
            <div className="min-w-0">
              <div className="font-bold truncate">{user.name}</div>
              <div className="text-xs text-muted-foreground truncate">{user.email}</div>
            </div>
          </div>
          <nav className="space-y-1">
            {links.map(({ to, label, icon: Icon, end, tid }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={tid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive ? "bg-primary text-primary-foreground" : "hover:bg-secondary"
                  }`
                }
              >
                <Icon className="w-4 h-4" /> {label}
              </NavLink>
            ))}
          </nav>
        </div>
      </aside>
      <main className="lg:col-span-9">
        <Outlet />
      </main>
    </div>
  );
};

export const DashboardHome = () => {
  const { user } = useAuth();
  const [myRests, setMyRests] = useState([]);
  const [myReviews, setMyReviews] = useState([]);

  useEffect(() => {
    api.get("/restaurants/owner/me").then(r => setMyRests(r.data)).catch(() => {});
    api.get(`/reviews?influencer_id=${user.user_id}`).then(r => setMyReviews(r.data)).catch(() => {});
  }, [user.user_id]);

  return (
    <div data-testid="dashboard-home">
      <h1 className="font-heading text-3xl font-black mb-2">Halo, {user.name?.split(" ")[0]}!</h1>
      <p className="text-muted-foreground mb-8">Kelola restoran, posting review, dan update profilmu.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="warm-card p-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Restoran Saya</p>
              <h2 className="font-heading text-3xl font-black mt-1">{myRests.length}</h2>
            </div>
            <Store className="w-10 h-10 text-primary" />
          </div>
          <Link to="/dashboard/restaurants">
            <Button variant="outline" size="sm" className="rounded-full mt-2" data-testid="manage-restaurants-btn">Kelola →</Button>
          </Link>
        </div>
        <div className="warm-card p-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Review Saya</p>
              <h2 className="font-heading text-3xl font-black mt-1">{myReviews.length}</h2>
            </div>
            <Star className="w-10 h-10 text-primary" />
          </div>
          <Link to="/dashboard/reviews">
            <Button variant="outline" size="sm" className="rounded-full mt-2" data-testid="manage-reviews-btn">Kelola →</Button>
          </Link>
        </div>
      </div>

      <div className="warm-card p-6 mt-6">
        <h3 className="font-heading font-bold text-lg mb-2">Aksi Cepat</h3>
        <div className="flex flex-wrap gap-3 mt-4">
          <Link to="/dashboard/restaurants/new">
            <Button className="rounded-full" data-testid="quick-new-restaurant"><Plus className="w-4 h-4 mr-2" />Tambah Restoran</Button>
          </Link>
          <Link to="/dashboard/reviews/new">
            <Button variant="outline" className="rounded-full" data-testid="quick-new-review"><Plus className="w-4 h-4 mr-2" />Tulis Review</Button>
          </Link>
          <Link to="/dashboard/featured">
            <Button variant="outline" className="rounded-full" data-testid="quick-featured"><Sparkles className="w-4 h-4 mr-2" />Featured Spot</Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;
